import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFilePdf,
    faFileWord,
    faFileExcel,
    faFileImage,
    faFile,
    faUpload,
    faMagnifyingGlass,
    faSort,
    faSortUp,
    faSortDown,
    faChevronLeft,
    faChevronRight,
    faRotate,
    faRobot,
    faSpinner,
    faDatabase
} from '@fortawesome/free-solid-svg-icons';
import UploadDocumentModal from './UploadDocumentModal';
import './RepositoryBrowser.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import DocumentDetailsModal from './DocumentDetailsModal';
import { toast } from 'react-hot-toast';
import apiHelper from '../../utils/apiHelper';
import * as pdfjsLib from 'pdfjs-dist';
import Modal from 'react-modal';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const RepositoryBrowser = ({ 
    items = [],
    selectedYear: propSelectedYear = new Date().getFullYear().toString(),
    onUpload,
    onEdit,
    onDelete,
    onDownload,
    onRefresh,
    onYearChange,
    onFetchAll,
    isLoading,
    indexingReport,
    showReportModal,
    setShowReportModal,
    handleIndexingReport
}) => {
    // Generate static years first
    const staticYears = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear + 1; year >= 2000; year--) {
            years.push(year.toString());
        }
        return years;
    }, []);


    const [searchTerm, setSearchTerm] = useState('');
    const [selectedYear, setSelectedYear] = useState(propSelectedYear);
    const [sortConfig, setSortConfig] = useState({ key: 'modified', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const itemsPerPage = 10;
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isIndexing, setIsIndexing] = useState(false);


    const safeItems = useMemo(() => {
        const safe = Array.isArray(items) ? items : [];
        return safe;
    }, [items]);

    const filteredAndSortedItems = useMemo(() => {
        const filtered = safeItems.filter(item => {
            const matchesSearch = !searchTerm || 
                (item.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (item.summary?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            
            return matchesSearch;
        });

        return filtered.sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];
            return sortConfig.direction === 'asc' 
                ? (aValue > bValue ? 1 : -1)
                : (aValue < bValue ? 1 : -1);
        });
    }, [safeItems, searchTerm, sortConfig]);

    const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => {
        setSelectedYear(propSelectedYear);
    }, [propSelectedYear]);



    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };


    const handleUploadClose = () => {
        setShowUploadModal(false);
    };

    const handleUploadComplete = async (formData) => {
        try {
           
            if (!onUpload) {
                throw new Error('Upload handler not provided');
            }

            // Call the parent's upload handler with the form data
            const uploadedDocument = await onUpload(formData);
            
            // Close the upload modal
            setShowUploadModal(false);
            
            // Refresh the current year's documents
            if (onYearChange) {
                await onYearChange(selectedYear);
            }

            return uploadedDocument; // Return the uploaded document
            
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Failed to upload document');
            throw error;
        }
    };

    const handleDownload = async (item) => {
        try {
            await onDownload(item.id);
        } catch (error) {
            console.error('Download error:', error);
            // Toast notification is handled in the service layer
        }
    };

    const handleDelete = async (item) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
            try {
                await onDelete(item.id);
                // Refresh will be handled by the parent component
            } catch (error) {
                console.error('Delete error:', error);
                // Toast notification is handled in the service layer
            }
        }
    };

    const handleEdit = async (updatedDocument) => {
        try {
            // Call the parent's onEdit handler
            await onEdit(updatedDocument);
            
            // Refresh the grid data first
            await handleRefresh();
            
            // Then update the selected document to keep modal open with fresh data
            setSelectedDocument(prev => ({
                ...prev,
                ...updatedDocument,
                modified: new Date().toISOString() // Update the modified timestamp
            }));
            
            // Show success message
            toast.success('Document updated successfully');
        } catch (error) {
            console.error('Edit error:', error);
            toast.error(error.message || 'Failed to update document');
        }
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
        }
        return faSort;
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await onRefresh();
        } catch (error) {
            console.error('Error in handleRefresh:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        const date = new Date(dateString);
        
        // Irish date format (dd/mm/yyyy)
        const shortDate = date.toLocaleDateString('en-IE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });

        // Full date and time for tooltip
        const fullDateTime = date.toLocaleDateString('en-IE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });

        return { shortDate, fullDateTime };
    };

    const handleYearChange = (event) => {
        const newYear = event.target.value;
        setSelectedYear(newYear);
        onYearChange?.(newYear);
    };

    const handleRowClick = (document) => {
        setSelectedDocument(document);
    };

    const getFileIcon = (fileType) => {
        switch (fileType?.toLowerCase()) {
            case 'pdf':
                return faFilePdf;
            case 'doc':
            case 'docx':
                return faFileWord;
            case 'xls':
            case 'xlsx':
                return faFileExcel;
            case 'jpg':
            case 'jpeg':
            case 'png':
                return faFileImage;
            default:
                return faFile;
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Add new function to handle bulk indexing
    const handleBulkIndex = async () => {
        if (!items || items.length === 0) {
            toast.error('No documents found to index');
            return;
        }

        if (!window.confirm(`Are you sure you want to index these documents?.`)) {
            return;
        }

        setIsIndexing(true);
        let successCount = 0;
        let errorCount = 0;
        const successes = [];
        const errors = [];

        // Month name mapping
        const monthMap = {
            'Jan': 'January', 'Feb': 'February', 'Mar': 'March',
            'Apr': 'April', 'May': 'May', 'Jun': 'June',
            'Jul': 'July', 'Aug': 'August', 'Sep': 'September',
            'Oct': 'October', 'Nov': 'November', 'Dec': 'December'
        };

        // Quarter calculation
        const getQuarterFromMonth = (month) => {
            if (!month) return '';
            const monthIndex = Object.values(monthMap).indexOf(month);
            if (monthIndex === -1) return '';
            return `Q${Math.floor(monthIndex / 3) + 1}`;
        };

        try {
            // Process documents one at a time with a delay
            for (const item of items) {
                try {
                  
                    const { fileContent } = await apiHelper.fetchPDFNameAndContentById(item.id);
                    
                    if (!fileContent) {
                        errorCount++;
                        continue;
                    }

                    // Convert blob to ArrayBuffer
                    const arrayBuffer = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsArrayBuffer(fileContent);
                    });

                    // Load PDF and extract text
                    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                    let fullText = '';
                    
                    for (let i = 1; i <= pdf.numPages; i++) {
                        const page = await pdf.getPage(i);
                        const textContent = await page.getTextContent();
                        const pageText = textContent.items.map(item => item.str).join(' ');
                        fullText += pageText + '\n';
                    }

                    if (!fullText) {

                        errorCount++;
                        continue;
                    }

                    // Convert short month to full month name
                    const fullMonth = monthMap[item.month] || item.month;
                    const calculatedQuarter = item.month ? getQuarterFromMonth(fullMonth) : '';

                    // Enhance the content with structured metadata
                    const enhancedContent = `[Date: ${fullMonth} ${item.year}]
[Type: Repository]
[Title: ${item.title}]
[Working Group: ${item.workingGroupName || item.workingGroup || 'None'}]
[Quarter: ${calculatedQuarter}]
${item.mpKey ? `[MP Key: ${item.mpKey}]` : ''}

${fullText}`;

                    const metadata = {
                        title: item.title,
                        source: item.title,
                        path: item.path,
                        size: item.size,
                        year: item.year,
                        workingGroup: item.workingGroup,
                        publishDate: item.publishDate,
                        documentId: String(item.id),
                        listItemId: String(item.listItemId),
                        summary: item.summary,
                        type: 'Repository',
                        fileUrl: item.webUrl,
                        text: enhancedContent,
                        content: enhancedContent
                    };

                    // Add a delay before indexing each document to avoid rate limiting
                    await new Promise(resolve => setTimeout(resolve, 5000));

                    await apiHelper.indexQdrantDocument(enhancedContent, metadata, item.id);
                    successCount++;
                    successes.push(item.title);

                    // Update progress in the UI using toast
                    toast(`Processed ${successCount + errorCount} of ${items.length} documents`, {
                        duration: 2000,
                        position: 'bottom-right'
                    });

                } catch (error) {
                    console.error(`Error processing document ${item.title}:`, error);
                    errors.push({ title: item.title, message: error.message || String(error) });
                    errorCount++;
                    
                    // If we hit a rate limit, wait longer before continuing
                    if (error.message?.includes('429') || error.message?.includes('rate limit')) {
                        await new Promise(resolve => setTimeout(resolve, 5000));
                    }
                }
            }

            const report = {
                successCount,
                errorCount,
                successes,
                errors
            };
            handleIndexingReport(report);

            if (successCount > 0) {
                toast.success(`Successfully indexed ${successCount} documents${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
                handleRefresh();
            } else {
                toast.error('No documents were successfully indexed');
            }
        } catch (error) {
            console.error('Error in bulk indexing process:', error);
            toast.error(error.message || 'Failed to index documents');
        } finally {
            setIsIndexing(false);
        }
    };

    return (
        <div className="repository-browser">
            <div className="repository-toolbar" style={{ opacity: isLoading ? 0.7 : 1 }}>
                <div className="search-filters">
                    <div className="search-box">
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <input
                            type="text"
                            placeholder="Search documents..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="filter-group">
                        <div className="year-select-wrapper">
                            <select 
                                value={selectedYear} 
                                onChange={handleYearChange}
                                className="year-filter"
                            >
                                <option value="all">All Years</option>
                                {staticYears.map(year => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="repository-actions">
                    <button 
                        className={`refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
                        onClick={handleRefresh}
                        disabled={isRefreshing || isLoading}
                        title="Refresh repository items"
                    >
                        <FontAwesomeIcon 
                            icon={faRotate} 
                            className={isRefreshing ? 'fa-spin' : ''} 
                        />
                        <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
                    </button>
                    {onFetchAll && (
                        <button 
                            className="fetch-all-btn"
                            onClick={onFetchAll}
                            disabled={isLoading}
                            title="Fetch all documents from all years"
                            style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: '1px solid #007bff',
                                backgroundColor: '#f8f9fa',
                                color: '#007bff',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s ease',
                                marginLeft: '8px'
                            }}
                        >
                            <FontAwesomeIcon icon={faDatabase} />
                            <span>Fetch All</span>
                        </button>
                    )}
                    <Tippy content="Upload new document">
                        <button 
                            className="upload-btn"
                            onClick={() => setShowUploadModal(true)}
                            disabled={isLoading}
                        >
                            <FontAwesomeIcon icon={faUpload} />
                        </button>
                    </Tippy>
                    {/* <Tippy content={`Index documents`}>
                        <button 
                            className="index-btn"
                            onClick={handleBulkIndex}
                            disabled={isLoading || isIndexing}
                        >
                            <FontAwesomeIcon 
                                icon={isIndexing ? faSpinner : faRobot} 
                                className={isIndexing ? 'fa-spin' : ''} 
                            />
                            <span>{isIndexing ? 'Indexing...' : 'Index'}</span>
                        </button>
                    </Tippy> */}
                </div>
            </div>

            {isLoading ? (
                <div className="loading-state">
                    <FontAwesomeIcon icon={faRotate} spin size="2x" />
                </div>
            ) : filteredAndSortedItems.length === 0 ? (
                <div className="empty-state">
                    <p>No documents found</p>
                    {searchTerm && <p>Try adjusting your search or filters</p>}
                </div>
            ) : (
                <div className="repository-table">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('title')}>
                                    Title <FontAwesomeIcon icon={getSortIcon('title')} />
                                </th>
                                <th onClick={() => handleSort('year')}>
                                    Year <FontAwesomeIcon icon={getSortIcon('year')} />
                                </th>
                                <th onClick={() => handleSort('type')}>
                                    Type <FontAwesomeIcon icon={getSortIcon('type')} />
                                </th>
                                <th onClick={() => handleSort('modified')}>
                                    Modified <FontAwesomeIcon icon={getSortIcon('modified')} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedItems.map(item => (
                                <tr key={item.id} onClick={() => handleRowClick(item)} style={{ cursor: 'pointer' }}>
                                    <td>
                                        <div className="document-info">
                                            <div className="document-details">
                                                <div className="document-title">{item.title || 'Untitled'}</div>
                                                {(item.workingGroup || item.summary) && (
                                                    <div className="document-meta">
                                                        {item.workingGroup && (
                                                            <span className="meta-item working-group">
                                                                {item.workingGroup}
                                                            </span>
                                                        )}
                                                        {item.summary && (
                                                            <Tippy 
                                                                content={item.summary}
                                                                placement="top-start"
                                                                maxWidth={600}
                                                                interactive={true}
                                                                appendTo={() => document.body}
                                                                zIndex={9999}
                                                            >
                                                                <span className="meta-item summary">
                                                                    {item.summary.length > 200
                                                                        ? `${item.summary.substring(0, 200)}...`
                                                                        : item.summary}
                                                                </span>
                                                            </Tippy>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{item.year || 'N/A'}</td>
                                    <td>{item.type || 'Document'}</td>
                                    <td>
                                        <Tippy content={formatDate(item.modified).fullDateTime}>
                                            <span>{formatDate(item.modified).shortDate}</span>
                                        </Tippy>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="pagination">
                <div className="pagination-info">
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)} of {filteredAndSortedItems.length} documents
                </div>
                <button 
                    className="pagination-btn" 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <span className="pagination-current">Page {currentPage} of {totalPages}</span>
                <button 
                    className="pagination-btn" 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>

            {showUploadModal && (
                <UploadDocumentModal
                    onClose={handleUploadClose}
                    onUpload={handleUploadComplete}
                    documentTypes={[
                        'Minutes',
                        'Report',
                        'Newsletter',
                        'Press Release',
                        'Publication',
                        'Other'
                    ]}
                    workingGroups={[
                        'Executive Committee',
                        'Technical Committee',
                        'Scientific Committee',
                        'Legal Committee',
                        'Communications Committee'
                    ]}
                />
            )}

            {selectedDocument && (
                <DocumentDetailsModal
                    document={selectedDocument}
                    onClose={() => setSelectedDocument(null)}
                    onDownload={onDownload}
                    onEdit={handleEdit}
                />
            )}
        </div>
    );
};

export default RepositoryBrowser; 